'use client';

import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  usePromotePost,
  usePurchaseAdsCredits,
} from '@/api/mutations/promotePost';

interface PromotePostDialogProps {
  postID: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PromotePostDialog({
  postID,
  open,
  onOpenChange,
}: PromotePostDialogProps) {
  const promoteMutation = usePromotePost();
  const purchaseAdsMutation = usePurchaseAdsCredits();
  const queryClient = useQueryClient();

  const isPending = promoteMutation.isPending || purchaseAdsMutation.isPending;

  const redirectToCheckout = (checkoutUrl: unknown) => {
    if (!checkoutUrl || typeof checkoutUrl !== 'string') {
      toast.error('Could not start checkout. Please try again.');
      return;
    }

    toast.success('Redirecting to checkout…');
    onOpenChange(false);
    window.location.href = checkoutUrl;
  };

  const handleConfirm = async () => {
    try {
      const res = await promoteMutation.mutateAsync({ postID });
      redirectToCheckout(res?.responseObject);
    } catch (error: any) {
      // No ad credits available — purchase them, then redirect to Stripe.
      if (error?.response?.status === 400) {
        try {
          const purchaseRes = await purchaseAdsMutation.mutateAsync();
          redirectToCheckout(purchaseRes?.responseObject);
        } catch (purchaseError: any) {
          toast.error(
            purchaseError?.response?.data?.message ||
              'Failed to start checkout'
          );
        }
        return;
      }

      // A promote can fail because the post is already promoted. Rather than
      // pattern-matching the message, refetch the user's promoted posts: if
      // that is the reason, the list now contains this post and the button
      // stops rendering.
      queryClient.invalidateQueries({ queryKey: ['posts', 'promoted', 'ids'] });

      toast.error(
        error?.response?.data?.message || 'Failed to start checkout'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-merriweather">
        <DialogHeader>
          <DialogTitle>Promote your post</DialogTitle>
          <DialogDescription className="text-black">
            Promote your post for 4 weeks for $150.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-3">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            className="rounded-full"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing…
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
